import * as THREE from 'three'
import BaseTool from '@glass-designer/tools/baseTool'
import BaseLineTool from '@glass-designer/tools/action/baseLineTool'
import EditingLine from '@glass-designer/objects/editingLine'
import GlassSplitLine from '@glass-designer/model/glassSplitLine'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import AngularDimension from '@glass-designer/objects/angularDimension'
import LinearDimension from '@glass-designer/objects/linearDimension'

import GlassDesignerTypes from '@glass-designer/types'
import { GlassTypes, AccountSettingKeys } from '@/store/types'

// Split tool
// Tool to add in vertical/horizontal splitting lines to stock-only panels
export default class SplitTool extends BaseLineTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.splitTool

    this.lineDetails.optionIsAxisAligned = true

    this.editingLine.setShowOutsideTextLabel(false)
    this.editingLine.setShowInsideTextLabel(false)
    this.editingLine.setUseAngles(false)
    
    this.traceOptions.ignoreSplitLines = false
    this.traceOptions.ignoreDatumLines = true
  }

  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    // Start point selections
    this.resetSplitLine()    

    this.displayToolSettingsPopover()
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    this.editingLine.setVisible(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected)
  }

  // Reset the split line process
  resetSplitLine() {
    this.editor.component.showToolHelpBox('fa-scalpel-path', 'Click and place the the spline line')
    
    this.resetLineDetails()

    this.lineDetails.startAngleFixed = true
    this.lineDetails.endAngleFixed = true

    this.editor.flagRenderRequired()
  }

  // Display the tool settings popover for this tool
  displayToolSettingsPopover() {
    var toolInfoText = ''
    let lineCreated = this.lineDetails.startPointSelected && this.lineDetails.endPointSelected

    let actionButtons = [
      { title: 'Rotate', icon: 'fa-undo', type: 'action', tag: 'rotate', disabled: !lineCreated },
      { title: 'Add Splitting Line', icon: 'fa-plus-square', type: 'action', tag: 'addSplit', disabled: !lineCreated },
    ]

    if(lineCreated) {
      let lineLength = MathsUtils.roundNumber(this.lineDetails.start.distanceTo(this.editingLine.endPoint), 0)

      // Show line length
      toolInfoText = `Split Line Length: ${lineLength}mm`
    }

    this.editor.component.displayToolSettingsPopover({
      title: 'Split Line Tool',
      actionButtons: actionButtons,
      infoText: toolInfoText,
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      actionCallback: (ctx, tag, info) => {
        if(tag == 'addSplit')
          ctx.addSplitLine()
        if(tag == 'rotate')
          ctx.rotateSplitLine()
      }
    })
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    super.onMouseDownEvent(ev, mouseData)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.lineDetails.settingEndPoint && ev.button == Config.Mouse.buttonSelect) {
      this._updateSplitLineEndpoint(mouseData)
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(ev.button == Config.Mouse.buttonSelect) {
      if(this._updateSplitLinePoints(mouseData)) {
        this.editor.flagRenderRequired()
        return true
      }
    }

    return false
  }

  onMouseWheelEvent(ev, mouseData) {
    super.onMouseWheelEvent(ev, mouseData)
  }

  onTouchStartEvent(ev, touchData) {
    super.onTouchStartEvent(ev, touchData)
  }
  onTouchEndEvent(ev, touchData) {
    super.onTouchEndEvent(ev, touchData)

    if(this._updateSplitLinePoints(touchData)) {
      this.editor.flagRenderRequired()
      return true
    }

    return false
  }
  onTouchCancelEvent(ev, touchData) {
    super.onTouchCancelEvent(ev, touchData)
  }
  onTouchMoveEvent(ev, touchData) {
    super.onTouchMoveEvent(ev, touchData)

    if(this.lineDetails.settingEndPoint) {

      this._updateSplitLineEndpoint(touchData)
    }
  }


  /**
   * Update the split line endpoint
   * Done during mouse move / touch move
   */
  _updateSplitLineEndpoint(mouseTouchData) {
    if(!this.selectedShape)
      return
      
    this.lineDetails.endPointSelected = true
    
    var nearestPoint = this.snapSplitToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y)) 
    nearestPoint = this.snapSplitLineToAngle(nearestPoint)

    this.lineDetails.end = nearestPoint

    // Update editing line
    this.editingLine.setStartPoint(this.lineDetails.start.clone())
    this.editingLine.setEndPoint(this.lineDetails.end.clone())    
    this.editingLine.startAngleFixed = true
    this.editingLine.endAngleFixed = true

    this.editor.flagRenderRequired()
  }

  /**
   * Update the split line start/end points from mouse/touch event
   * Returns true if the split line was updated & the mouse/touch event was processed
   */
  _updateSplitLinePoints(mouseTouchData) {
    if(!this.lineDetails.startPointSelected) {
      if(mouseTouchData.isSnapGridEnabled)
        this.lineDetails.start = mouseTouchData.snappedPosition.clone()
      else
        this.lineDetails.start = mouseTouchData.position.clone()

      // Get shape closest to the split line
      this.selectedShape = this.editor.findClosestGlassShape(this.lineDetails.start)
      if(!this.selectedShape)
        return true

      // If not a stock-only shape, show error & stop adding
      if(!this.selectedShape.isStockLinked()) {
        this.editor.component.errorMessage('Cannot add split lines', 'Split lines can only be added to glass panels flagged as stock only')
        return true
      }

      // JB 31/10/2022 : As per KC, Can now add in any amount of splits but if it goes over the limit it'll flag a guidelineWarning & therefore a production warning      
      // No longer editing end point when drawing splits. Just 1 click -> create split editing line automatically
      this.lineDetails.startPointSelected = true
      this.lineDetails.settingEndPoint = false

      this.selectedShapeBounds = this.selectedShape.calculateSegmentBounds()
      this.selectedShapeBounds.getCenter(this.selectedShapeCenter)

      // Attempt to snap to the nearest vertex on the shape
      let nearestPoint = this.snapSplitToNearestShapePoint(this.selectedShape, mouseTouchData.position.clone()) 
      this.lineDetails.start = nearestPoint

      // Figure out if the split line is horizontal/vertical based on the start point set on the shape
      this.lineDetails.isHorizontal = false
      let vertCheckMinDist = 2
      if(Math.abs(this.selectedShapeBounds.min.x - this.lineDetails.start.x) < vertCheckMinDist || Math.abs(this.selectedShapeBounds.max.x - this.lineDetails.start.x) < vertCheckMinDist) {
        this.lineDetails.isHorizontal = true
      }

      // Update the endpoint automatically
      this._updateSplitLineEndpoint(mouseTouchData)
      this.updateSplitLineToTracedLine()

      this.traceEditingLine({ updateStartPoint: true, updateEndPoint: true, roundPoint: true })
      this.updateEditingLineLinkedObjects(true, true)

      // Setup dimensions for the selected shape & editing line
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update settings popover
      this.displayToolSettingsPopover()

      // Disable the editing line from touch/mouse events to stop it receiving clicks
      this.editingLine.setEnabled(true)
      return true
    }
  }

  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == this.editingLine.startHandle) {

        // Move end handle too
        let moveVec = this.editingLine.startHandle.position.clone().sub(this.lineDetails.start)
        this.lineDetails.end.add(moveVec)
        this.lineDetails.start.set(this.editingLine.startHandle.position.x, this.editingLine.startHandle.position.y, this.lineDetails.start.z)

        this.traceEditingLine({ updateStartPoint: true, updateEndPoint: true, referencePoint: this.editingLine.startHandle.position, roundPoint: true })
        this.updateEditingLineLinkedObjects(true, true)

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])
        
        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine.endHandle) {

        // Move start handle too
        let moveVec = this.editingLine.endHandle.position.clone().sub(this.lineDetails.end)
        this.lineDetails.start.add(moveVec)
        this.lineDetails.end.set(this.editingLine.endHandle.position.x, this.editingLine.endHandle.position.y, this.lineDetails.end.z)

        this.traceEditingLine({ updateStartPoint: true, updateEndPoint: true, referencePoint: this.editingLine.endHandle.position, roundPoint: true })
        this.updateEditingLineLinkedObjects(true, true)
        
        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine) {

        this.lineDetails.start.set(data.startPoint.x, data.startPoint.y, this.lineDetails.start.z)
        this.lineDetails.end.set(data.endPoint.x, data.endPoint.y, this.lineDetails.end.z)

        this.traceEditingLine({ updateStartPoint: true, updateEndPoint: true, referencePoint: data.grabPoint, roundPoint: true })
        this.updateEditingLineLinkedObjects(true, true)
        this.updateSplitLineToTracedLine()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }

    }

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectReleased) {

      // Set the split line to match the final traced values
      this.updateSplitLineToTracedLine()

      // Update dimensions after moving objects
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update info on popover
      this.displayToolSettingsPopover()
    }
  }


  /**
   * Update the split line settings to match the final raytraced points
   */
  updateSplitLineToTracedLine() {
    this.lineDetails.start = this.editingLine.startPoint.clone()
    this.lineDetails.end = this.editingLine.endPoint.clone()
  }


  /**
   * Snap a point to the nearest point on a shape
   * Returns a Vector2 object for the nearest point
   */
  snapSplitToNearestShapePoint(shape, position) {
    let closestInfo = shape.calculateClosestEdgePoint(position, { ignoreHoles: true, ignoreDatumLines: true })
    if(closestInfo)
      return closestInfo.point
    return position
  }


  /**
   * Split lines need to be either horizontal or vertical
   * Snap to the nearest horizontal/vertical orientation
   */
  snapSplitLineToAngle(currentSnapPoint) {
    var startPoint = this.lineDetails.start.clone()
    var endPoint = this.lineDetails.end.clone()

    let castLength = Math.max(this.selectedShapeBounds.max.x - this.selectedShapeBounds.min.x, this.selectedShapeBounds.max.y - this.selectedShapeBounds.min.y) * 2

    if(this.lineDetails.isHorizontal) {
      endPoint.y = startPoint.y
      if(startPoint.x < this.selectedShapeCenter.x) {
        endPoint.x = startPoint.x + castLength
        startPoint.x -= castLength
      } else {
        endPoint.x = startPoint.x - castLength
        startPoint.x += castLength
      }
    } else {
      endPoint.x = startPoint.x

      if(startPoint.y < this.selectedShapeCenter.y) {
        endPoint.y = startPoint.y + castLength
        startPoint.y -= castLength
      } else {
        endPoint.y = startPoint.y - castLength
        startPoint.y += castLength
      }
    }

    var lineDir = endPoint.clone().sub(startPoint)
    lineDir.normalize()

    // Trace line from other point through the shape
    var intersectionList = this.selectedShape.calculateIntersectionWithLine(startPoint, endPoint, { ignoreDatumLines: true })

    // Get first point after start point. Should be the first split line hit or the segment
    // List should be sorted by calculateIntersectionWithLine()
    if(intersectionList.length > 0) {
      for(var intPoint of intersectionList) {
        if(MathsUtils.calculateDistanceToPlane2D(lineDir, this.lineDetails.start, intPoint) > 0)
          return intPoint
      }
    }

    return currentSnapPoint
  }



  /**
   * Rotate the split line to switch between vertical & horizontal
   */
  rotateSplitLine() {
    if(this.lineDetails.isHorizontal) {
      this.lineDetails.start.set(this.selectedShapeCenter.x, this.selectedShapeBounds.min.y)
      this.lineDetails.end.set(this.selectedShapeCenter.x, this.selectedShapeBounds.max.y)
    } else {
      this.lineDetails.start.set(this.selectedShapeBounds.min.x, this.selectedShapeCenter.y)
      this.lineDetails.end.set(this.selectedShapeBounds.max.x, this.selectedShapeCenter.y)
    }

    this.lineDetails.isHorizontal = !this.lineDetails.isHorizontal

    this.traceEditingLine({ updateStartPoint: true, updateEndPoint: true, referencePoint: this.selectedShapeCenter, roundPoint: true })
    this.updateEditingLineLinkedObjects(true, true)
    this.updateSplitLineToTracedLine()

     // Update editing line
     this.editingLine.setStartPoint(this.lineDetails.start.clone())
     this.editingLine.setEndPoint(this.lineDetails.end.clone())    
     this.editingLine.startAngleFixed = true
     this.editingLine.endAngleFixed = true
     this.editor.flagRenderRequired()

     // Setup dimensions for the selected shape & editing line
     this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

     // Update info on popover
     this.displayToolSettingsPopover()
  }


  /**
   * Add the line as a glass splitting line to the shape
   */
  addSplitLine() {
    this.editor.startUndoGroup('Adding Splitting Line')
    this.editor.addUndoShape(this.selectedShape)

    var shapeBounds = this.selectedShape.calculateSegmentBounds()

    var splitLine = new GlassSplitLine(this.editor, {
      parentOffset: this.editingLine.startPoint.round().clone().sub(shapeBounds.min),
      startPosition: this.editingLine.startPoint.round(),
      endPosition: this.editingLine.endPoint.round(),
    })
    
    this.selectedShape.addSplitLine(splitLine)
    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)
    this.editor.updateShapeDimensions(this.selectedShape)
    this.editor.render()

    // Finish
    this.closeTool()
  }

}
